import React from 'react';
// 引入柱状图
import 'echarts/lib/chart/line';
// 引入提示框和标题组件
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/title';
// 使用样式
import 'echarts/theme/macarons';
import { yAxisFormat, reviewsFormat } from '@/pages/opinion/utils/value';
import EChartCommon from '@/pages/opinion/components/EChartCommon';
import { formatChartParamsDate } from '@/pages/opinion/components/ChartCommon/formatter';
import { useIntl } from 'umi';
import { othersList } from '@op/pages/stats/constants';
import { getPercentageInfo } from '@op/utils';

export default function SentimentLineChart({
  isDetail,
  height = 230,
  loading,
  chartData,
  filter,
  handleOnClick,
  transFunc = (name: string) => name,
  isTopic,
}: any) {
  const { formatMessage } = useIntl();
  const initMentions = () => {
    const dataY: any = [];
    const dataOne: any = [];
    const dataTow: any = [];
    const dataThree: any = [];
    const total: any = [];
 
    chartData?.map(
      (v: {
        name: string;
        negative_mentions: number;
        neutral_mentions: any;
        positive_mentions: any;
      }) => {
        if (isTopic && othersList.includes(v.name)) return;
        const totalValue =
          v.neutral_mentions + v.positive_mentions + v.negative_mentions;
        dataY.unshift(transFunc(v.name));
        dataOne.unshift({
          value: v.negative_mentions * -1,
          originName: v.name,
          total: totalValue,
        }); // 转成负数，方便形成有正负值的坐标系
        dataTow.unshift({
          value: v.neutral_mentions,
          originName: v.name,
        });
        dataThree.unshift({
          value: v.positive_mentions,
          originName: v.name,
        });
        total.unshift(totalValue);
      },
    );

    const showNeutral =
      dataOne.every((item: { value: number }) => item.value === 0) &&
      dataThree.every((item: { value: number }) => item.value === 0);

    const option = {
      tooltip: {
        extraCssText: 'z-index:8',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        confine: true,
        trigger: 'axis',
        formatter: (params: any) => {
          return formatChartParamsDate({
            title: params?.[0]?.axisValue,
            rightTitle: formatMessage({ id: 'Mentions' }),
            params,
            listTitleFunc: (item) => formatMessage({ id: item.seriesName }),
            valueFormat: (value) => reviewsFormat(Math.abs(value)),
          });
        },
        triggerOn: isDetail ? 'click' : 'mousemove',
        axisPointer: {
          type: 'shadow',
        },
      },
      grid: {
        left: 0,
        right: 0,
        contentLabel: true,
      },
      legend: {
        data: [
          {
            name: 'Negative',
            itemStyle: { color: '#F17C86' },
          },
          {
            name: 'Neutral',
            itemStyle: { color: '#6E7794' },
          },
          {
            name: 'Positive',
            itemStyle: { color: '#69D19C' },
          },
        ],
        selected: {
          Negative: true,
          Neutral: showNeutral,
          Positive: true,
        },
        formatter: function (name: string) {
          return formatMessage({ id: name });
        },
      },
      xAxis: {
        type: 'value',
        splitLine: {
          show: true,
          lineStyle: { color: '#E5E5E5', type: 'dashed' },
        },
        axisLine: {
          show: true,
          lineStyle: { color: '#E5E5E5' },
        },
        axisLabel: {
          formatter: (value: number) => yAxisFormat(Math.abs(value)), // 负值转成正值
        },
        boundaryGap: ['10%', 0],
      },
      yAxis: [
        {
          type: 'category',
          position: 'left',
          axisLabel: {
            interval: 0,
            show: true,
            formatter: (value: any) => `{label|${value}}`,
            rich: {
              label: {
                align: 'left',
              },
            },
            padding: [0, 0, 0, 0],
            overflow: 'break',
            fontWeight: 400,
            fontSize: 12,
            lineHeight: 16,
          },
          splitLine: {
            show: false,
            lineStyle: { color: '#E5E5E5', type: 'dashed' },
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          data: dataY,
        },
        {
          type: 'category',
          position: 'right',
          data: total,
          axisLabel: {
            formatter: (value) => `{value|${yAxisFormat(value)}}`,
            show: true,
            width: 40,
            rich: {
              value: {
                align: 'right',
              },
            },
            fontWeight: 400,
            fontSize: 12,
            lineHeight: 16,
            padding: [0, 0, 0, 0],
          },
          splitLine: {
            show: false,
            lineStyle: { color: '#E5E5E5', type: 'dashed' },
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
      ],
      series: [
        {
          name: 'Negative',
          type: 'bar',
          stack: '1',
          color: '#F17C86',
          barMaxWidth: 30,
          label: {
            show: true,
            position: 'left',
            formatter: (params: {
              value: number;
              data: { total: number };
              dataIndex: number;
            }) => {
              const total = params.data.total;
              const value = Math.abs(params.value);
              if (value) {
                return getPercentageInfo(value, total, 2);
              }
              return '';
            },
          },
          emphasis: {
            disable: true,
          },
          data: dataOne,
        },
        {
          name: 'Neutral',
          type: 'bar',
          stack: '1',
          color: '#6E7794',
          barMaxWidth: 30,
          label: {
            show: false,
            position: 'right',
            fontSize: 10,
            formatter: (_params: any) => {},
          },
          data: dataTow,
        },
        {
          name: 'Positive',
          type: 'bar',
          stack: '1',
          color: '#69D19C',
          barMaxWidth: 30,
          data: dataThree,
        },
      ],
    };

    return option;
  };

  const options = initMentions();

  return (
    <EChartCommon
      empty={!chartData || chartData.length === 0}
      options={options}
      loading={loading}
      height={height}
      optionsProps={{ type: 'bar', setMaxBarWidth: false }}
      rightTitle={formatMessage({ id: 'Mentions' })}
      eventsDict={{
        click: (params) => {
          // console.log(params);
          handleOnClick?.(params);
        },
      }}
    ></EChartCommon>
  );
}
